import { useEffect, useRef, useState, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import './ThemePicker.css'

/* ── Color helpers ───────────────────────────────────────── */
function hexToRgb(hex: string): { r: number; g: number; b: number } {
  let h = String(hex || '').replace('#', '')
  if (h.length === 3) h = h.split('').map(c => c + c).join('')
  const n = parseInt(h, 16)
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 }
}
function toHex(n: number): string {
  const s = Math.max(0, Math.min(255, Math.round(n))).toString(16)
  return s.length === 1 ? '0' + s : s
}
function mix(hex: string, amt: number): string {
  // amt in [-1, +1]; negative = darker, positive = lighter
  const c = hexToRgb(hex)
  const t = amt < 0 ? 0 : 255
  const p = Math.abs(amt)
  return '#' + toHex(c.r + (t - c.r) * p) + toHex(c.g + (t - c.g) * p) + toHex(c.b + (t - c.b) * p)
}
function luminance(hex: string): number {
  const c = hexToRgb(hex)
  return (0.299 * c.r + 0.587 * c.g + 0.114 * c.b) / 255
}
function onPrimary(hex: string): string {
  return luminance(hex) > 0.6 ? '#1A1D22' : '#ffffff'
}

/* ── Presets ─────────────────────────────────────────────── */
const PRESETS: Record<string, { primary: string; active: string; swatch?: string }> = {
  classic: { primary: '#bab09a', active: '#6d8a90' },
  // petrol's identity is the navy nav gradient; `primary` = the navy (#1e4e79,
  // matches the topnav / --brand-primary-2) shown in the colour picker;
  // `swatch` = the gradient preview circle.
  petrol:  { primary: '#1e4e79', active: '#2A6F97', swatch: 'linear-gradient(180deg, #163754, #1e4e79)' },
  rose:    { primary: '#B0697E', active: '#F1E0E5' },
}

/* Palette reference groups — each swatch reads the LIVE theme token via
   getComputedStyle, so the reference always reflects the active theme. */
const PALETTE_GROUPS: { title: string; tokens: { name: string; v: string }[] }[] = [
  { title: 'Ink · Text', tokens: [
    { name: 'ink', v: '--ink' }, { name: 'ink-2', v: '--ink-2' },
    { name: 'brand', v: '--brand-primary' }, { name: 'brand-2', v: '--brand-primary-2' },
    { name: 'mute', v: '--mute' }, { name: 'mute-2', v: '--mute-2' },
  ] },
  { title: 'Surfaces · Sand', tokens: [
    { name: 'surface', v: '--brand-surface' }, { name: 'soft', v: '--brand-surface-soft' },
    { name: 'warm', v: '--brand-surface-warm' }, { name: 'sand', v: '--sand' },
    { name: 'sand-2', v: '--sand-2' }, { name: 'sand-3', v: '--sand-3' },
    { name: 'accent', v: '--cream-accent' }, { name: 'eyebrow', v: '--brand-eyebrow' },
  ] },
  { title: 'Borders · Lines', tokens: [
    { name: 'line', v: '--line' }, { name: 'line-2', v: '--line-2' },
    { name: 'brand-line', v: '--brand-line' },
  ] },
  { title: 'Status', tokens: [
    { name: 'success', v: '--success' }, { name: 'success-bg', v: '--success-50' },
    { name: 'warn', v: '--warn' }, { name: 'warn-bg', v: '--warn-50' },
    { name: 'danger', v: '--danger' }, { name: 'danger-bg', v: '--danger-50' },
  ] },
]

const STORAGE_KEY = 'mashfa-theme'

/* Full HeilPort-logo colour set — each maps to a CSS var the inline SVG (MashfaLogo) reads. */
const LOGO_COLORS = [
  { key: 'halo',  cssVar: '--logo-halo',   label: 'Halo',          def: '#e8d7a8' },
  { key: 'disc1', cssVar: '--logo-disc-1', label: 'Scheibe oben',  def: '#3a3026' },
  { key: 'disc2', cssVar: '--logo-disc-2', label: 'Scheibe unten', def: '#1c160f' },
  { key: 'cross', cssVar: '--logo-cross',  label: 'Kreuz',         def: '#c9b364' },
  { key: 'heil',  cssVar: '--logo-heil',   label: 'Heil',          def: '#c9b364' },
  { key: 'port',  cssVar: '--logo-port',   label: 'Port',          def: '#c9b364' },
] as const
const LOGO_DEFAULTS: Record<string, string> = Object.fromEntries(LOGO_COLORS.map(c => [c.key, c.def]))

const INLINE_VARS = [
  '--brand-primary', '--brand-primary-2', '--brand-on-primary',
  '--event-active-bg', '--event-active-fg', '--event-active-border',
  '--hero-from', '--hero-to',
  ...LOGO_COLORS.map(c => c.cssVar),
]

type ThemeOpts = { preset: string; primary?: string; active?: string; logo?: Record<string, string>; logoText?: { t1: string; t2: string } }

export default function ThemePicker({ onOpen }: { onOpen?: () => void } = {}) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [activePreset, setActivePreset] = useState<string | null>('classic')
  const [primaryColor, setPrimaryColor] = useState('var(--ink)')
  const [activeColor, setActiveColor] = useState('var(--ink)')
  const [logoColors, setLogoColors] = useState<Record<string, string>>(LOGO_DEFAULTS)
  const [logoText, setLogoText] = useState<{ t1: string; t2: string }>({ t1: 'HEIL', t2: 'PORT' })

  const panelRef = useRef<HTMLDivElement>(null)
  const fabRef = useRef<HTMLButtonElement>(null)

  /* ── Apply theme ──────────────────────────────────────── */
  const applyTheme = useCallback((opts: ThemeOpts) => {
    const html = document.documentElement
    const preset = opts.preset || 'classic'
    const presetDefaults = PRESETS[preset] || PRESETS.classic

    // Clear inline vars first (start clean)
    INLINE_VARS.forEach(v => html.style.removeProperty(v))

    // data-theme always reflects the chosen preset — keeps hero gradient,
    // logo variant, and other [data-theme="..."] styling intact.
    html.setAttribute('data-theme', preset)
    setActivePreset(preset)
    // keep the browser-tab favicon in sync with the theme
    const favEl = document.getElementById('app-favicon')
    if (favEl) favEl.setAttribute('href', `/favicon-${preset}.svg`)

    const primary = opts.primary || presetDefaults.primary
    const active = opts.active || presetDefaults.active
    setPrimaryColor(primary)
    setActiveColor(active)

    // Apply inline overrides on top of the preset whenever a custom color is set.
    // Inline styles win over [data-theme="..."] CSS, so primary/active swap cleanly.
    if (opts.primary || opts.active) {
      html.style.setProperty('--brand-primary', primary)
      html.style.setProperty('--brand-primary-2', mix(primary, -0.2))
      html.style.setProperty('--brand-on-primary', onPrimary(primary))
      html.style.setProperty('--event-active-bg', active)
      html.style.setProperty('--event-active-fg', onPrimary(active))
      html.style.setProperty('--event-active-border', mix(active, -0.15))
      // Hero gradient — derived from primary so the dashboard banner follows the picker
      html.style.setProperty('--hero-from', mix(primary, -0.35))
      html.style.setProperty('--hero-to', mix(primary, 0.15))
    }

    // Logo colours — full set (halo, disc top/bottom, cross, Heil, Port), independent of preset.
    const logo = { ...LOGO_DEFAULTS, ...(opts.logo || {}) }
    setLogoColors(logo)
    if (opts.logo) LOGO_COLORS.forEach(c => {
      const v = opts.logo![c.key]
      if (v) html.style.setProperty(c.cssVar, v)
    })
    // Logo wordmark text — MashfaLogo reads it from localStorage (text can't be a CSS var).
    setLogoText(opts.logoText || { t1: 'HEIL', t2: 'PORT' })

    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(opts))
    } catch { /* ignore */ }
    window.dispatchEvent(new Event('mashfa-logo-text'))
  }, [])

  /* ── Init: URL > localStorage > Classic ───────────────── */
  useEffect(() => {
    const url = new URLSearchParams(window.location.search).get('theme')
    if (url && PRESETS[url]) {
      applyTheme({ preset: url })
      return
    }
    try {
      const raw = localStorage.getItem(STORAGE_KEY)
      if (raw) {
        const stored = JSON.parse(raw) as ThemeOpts
        if (stored && stored.preset) {
          applyTheme(stored)
          return
        }
      }
    } catch { /* ignore */ }
    // Default: classic (already set in CSS :root, just mark active)
    document.documentElement.setAttribute('data-theme', 'classic')
    setActivePreset('classic')
  }, [applyTheme])

  /* ── Close on outside click ───────────────────────────── */
  useEffect(() => {
    if (!open) return
    const onDocClick = (e: MouseEvent) => {
      if (panelRef.current?.contains(e.target as Node)) return
      if (fabRef.current?.contains(e.target as Node)) return
      setOpen(false)
    }
    document.addEventListener('click', onDocClick)
    return () => document.removeEventListener('click', onDocClick)
  }, [open])

  const handleColorChange = (which: 'primary' | 'active', value: string) => {
    // Classic preset is locked — customization only on petrol/rose.
    if (activePreset === 'classic') return
    const preset = activePreset || 'classic'
    const next = which === 'primary'
      ? { preset, primary: value, active: activeColor, logo: logoColors, logoText }
      : { preset, primary: primaryColor, active: value, logo: logoColors, logoText }
    applyTheme(next)
  }

  const handleLogoColor = (key: string, value: string) => {
    const preset = activePreset || 'classic'
    const opts: ThemeOpts = { preset, logo: { ...logoColors, [key]: value }, logoText }
    if (preset !== 'classic') { opts.primary = primaryColor; opts.active = activeColor }
    applyTheme(opts)
  }

  const handleLogoText = (part: 't1' | 't2', value: string) => {
    const preset = activePreset || 'classic'
    const opts: ThemeOpts = { preset, logo: logoColors, logoText: { ...logoText, [part]: value } }
    if (preset !== 'classic') { opts.primary = primaryColor; opts.active = activeColor }
    applyTheme(opts)
  }

  const handleReset = () => {
    try { localStorage.removeItem(STORAGE_KEY) } catch { /* ignore */ }
    applyTheme({ preset: 'classic' })
  }

  return (
    <>
      <button
        ref={fabRef}
        type="button"
        className="nav-submenu-item theme-trigger"
        aria-label={t('themePicker.title')}
        onClick={e => { e.stopPropagation(); const next = !open; setOpen(next); if (next) onOpen?.() }}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="9"/>
          <circle cx="7" cy="11" r="1.4" fill="currentColor"/>
          <circle cx="11" cy="7" r="1.4" fill="currentColor"/>
          <circle cx="16.5" cy="9" r="1.4" fill="currentColor"/>
          <circle cx="17" cy="14.5" r="1.4" fill="currentColor"/>
          <path d="M12 21c1.3 0 2-.9 2-2 0-1.5-1.5-1.7-1.5-3 0-.8.7-1.5 1.5-1.5h.5"/>
        </svg>
        <span className="theme-trigger-label">{t('themePicker.title')}</span>
      </button>

      {createPortal(
      <div
        ref={panelRef}
        className={`theme-panel${open ? ' open' : ''}`}
        role="dialog"
        aria-label={t('themePicker.title')}
      >
        <div className="theme-panel-head">
          <span className="theme-panel-title">{t('themePicker.title')}</span>
          <button type="button" className="theme-panel-close" aria-label={t('themePicker.close')} onClick={() => setOpen(false)}>×</button>
        </div>

        <div className="theme-section-label">{t('themePicker.presets')}</div>
        <div className="theme-presets">
          {(['classic', 'petrol', 'rose'] as const).map(key => (
            <button
              key={key}
              type="button"
              className={`theme-preset${activePreset === key ? ' active' : ''}`}
              onClick={() => applyTheme({ preset: key })}
            >
              <span
                className="theme-preset-circle"
                style={{ background: PRESETS[key].swatch ?? PRESETS[key].primary }}
              />
              <span className="theme-preset-name">
                {key === 'classic' ? 'Classic' : key === 'petrol' ? 'Petrol' : 'Rosé'}
              </span>
            </button>
          ))}
        </div>

        <div className="theme-section-label">{t('themePicker.customColors')}</div>
        <div className={`theme-color-row${activePreset === 'classic' ? ' is-locked' : ''}`}>
          <label htmlFor="cpPrimary">{t('themePicker.primaryColor')}</label>
          <span className="theme-hex">{primaryColor.toUpperCase()}</span>
          <input
            type="color"
            id="cpPrimary"
            value={primaryColor}
            disabled={activePreset === 'classic'}
            onChange={e => handleColorChange('primary', e.target.value)}
          />
        </div>
        {activePreset === 'classic' && (
          <p className="theme-locked-hint">
            {t('themePicker.lockedHint')}
          </p>
        )}

        {/* ── Full logo colours — halo, disc, cross, Heil, Port (live, persisted) ── */}
        <div className="theme-section-label">{t('themePicker.logoColors', 'Logo')}</div>
        {LOGO_COLORS.map(c => (
          <div className="theme-color-row" key={c.key}>
            <label htmlFor={`cp-${c.key}`}>{c.label}</label>
            <span className="theme-hex">{(logoColors[c.key] || '').toUpperCase()}</span>
            <input type="color" id={`cp-${c.key}`} value={logoColors[c.key]} onChange={e => handleLogoColor(c.key, e.target.value)} />
          </div>
        ))}
        <div className="theme-color-row">
          <label htmlFor="cpText1">Text 1</label>
          <input type="text" id="cpText1" value={logoText.t1} maxLength={24}
            onChange={e => handleLogoText('t1', e.target.value)}
            style={{ flex: '0 0 130px', minWidth: 0, fontFamily: 'inherit', fontSize: '12px', padding: '5px 8px', border: '1px solid var(--brand-line)', borderRadius: '6px' }} />
        </div>
        <div className="theme-color-row">
          <label htmlFor="cpText2">Text 2</label>
          <input type="text" id="cpText2" value={logoText.t2} maxLength={24}
            onChange={e => handleLogoText('t2', e.target.value)}
            style={{ flex: '0 0 130px', minWidth: 0, fontFamily: 'inherit', fontSize: '12px', padding: '5px 8px', border: '1px solid var(--brand-line)', borderRadius: '6px' }} />
        </div>

        {/* ── Palette reference (reads live theme tokens) ──────────────── */}
        <div className="theme-section-label">{t('themePicker.palette')}</div>

        {PALETTE_GROUPS.map(group => (
          <div className="theme-palette-group" key={group.title}>
            <div className="theme-palette-group-head">{group.title}</div>
            <div className="theme-palette-grid">
              {group.tokens.map(tk => (
                <ColorSwatch key={tk.v} name={tk.name} cssVar={tk.v} themeKey={activePreset} />
              ))}
            </div>
          </div>
        ))}

        <div className="theme-palette-group">
          <div className="theme-palette-group-head">Theme · Presets</div>
          <div className="theme-palette-grid">
            <FixedSwatch name="classic" hex="#bab09a" />
            <FixedSwatch name="petrol" hex="#1e4e79" />
            <FixedSwatch name="rosé" hex="#B0697E" />
          </div>
        </div>

        <button type="button" className="theme-reset" onClick={handleReset}>
          {t('themePicker.reset')}
        </button>
      </div>,
        document.body,
      )}
    </>
  )
}

/** Swatch that reads a LIVE theme token (getComputedStyle) — copies hex on click.
 *  Re-reads whenever the active theme (themeKey) changes. */
function ColorSwatch({ name, cssVar, themeKey }: { name: string; cssVar: string; themeKey: string | null }) {
  const [hex, setHex] = useState('')
  useEffect(() => {
    const v = getComputedStyle(document.documentElement).getPropertyValue(cssVar).trim()
    setHex(v)
  }, [cssVar, themeKey])
  const handleCopy = () => {
    try { navigator.clipboard.writeText(hex) } catch { /* ignore */ }
  }
  return (
    <button type="button" className="theme-swatch" onClick={handleCopy} title={`${hex} · copy`}>
      <span className="theme-swatch-chip" style={{ background: `var(${cssVar})` }} />
      <span className="theme-swatch-label">{name}</span>
      <span className="theme-swatch-hex">{hex.toUpperCase()}</span>
    </button>
  )
}

/** Fixed-colour swatch — used for the 3 theme-preset representative chips. */
function FixedSwatch({ name, hex }: { name: string; hex: string }) {
  const handleCopy = () => {
    try { navigator.clipboard.writeText(hex) } catch { /* ignore */ }
  }
  return (
    <button type="button" className="theme-swatch" onClick={handleCopy} title={`${hex} · copy`}>
      <span className="theme-swatch-chip" style={{ background: hex }} />
      <span className="theme-swatch-label">{name}</span>
      <span className="theme-swatch-hex">{hex.toUpperCase()}</span>
    </button>
  )
}
